import React from 'react';
import cx from 'classnames';

import useUniqueId from '../utils/useUniqueId';

import TableHead, { TableHeadProps } from './TableHead';
import TableHeadCell, { TableHeadCellProps } from './TableHeadCell';
import TableBody, { TableBodyProps } from './TableBody';
import TableRow, { TableRowProps } from './TableRow';
import TableCell, { TableCellProps } from './TableCell';
import TableRowExpanded, { TableRowExpandedProps } from './TableRowExpanded';
import TableExpandedGroup, {
  TableExpandedGroupProps,
} from './TableExpandedGroup';
import TableExpandedList, { TableExpandedListProps } from './TableExpandedList';

import styles from './Table.module.scss';
export interface Props
  extends Omit<React.HTMLProps<HTMLTableElement>, 'className'> {
  /** Value of table which should be a Table.Head and Table.Body.*/
  children: [
    React.ReactElement<TableHeadProps>,
    React.ReactElement<TableBodyProps>,
  ];
  /** Accessible name for the Table, which will be visually hidden by default. */
  label: string;
  /** Apply CSS class to the container. */
  className?: Parameters<typeof cx>[0];
}

export interface TableComposition {
  Head: React.FC<TableHeadProps>;
  HeadCell: React.FC<TableHeadCellProps>;
  Body: React.FC<TableBodyProps>;
  Row: React.FC<TableRowProps>;
  Cell: React.FC<TableCellProps>;
  RowExpanded: React.FC<TableRowExpandedProps>;
  ExpandedGroup: React.FC<TableExpandedGroupProps>;
  ExpandedList: React.FC<TableExpandedListProps>;
}

export const Table: React.FC<Props> & TableComposition = ({
  children,
  className,
  label,
  id,
  ...restProps
}) => {
  const uid = useUniqueId();

  return (
    <table
      {...restProps}
      aria-labelledby={uid}
      className={cx(styles.table, className)}
    >
      <caption id={uid} className={cx(styles.caption)}>
        {label}
      </caption>
      {children}
    </table>
  );
};

Table.displayName = 'Table';

Table.Head = TableHead;
Table.HeadCell = TableHeadCell;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.RowExpanded = TableRowExpanded;
Table.ExpandedGroup = TableExpandedGroup;
Table.ExpandedList = TableExpandedList;

export default Table;
export { styles };
